import logoTransparent from '../logo-transparent.png';
import { useLocation } from 'react-router';
import { Link } from 'react-router';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faClose, faLanguage } from '@fortawesome/free-solid-svg-icons';
import { translations } from '../translations/header';

export function Header() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 25) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const location = useLocation();

    const getLocalePrefix = () => {
        if (location.pathname === "/" || location.pathname.startsWith("/GE")) return "/GE";
        if (location.pathname.startsWith("/EN")) return "/EN";
        if (location.pathname.startsWith("/RU")) return "/RU";
        return "/GE";
    };

    const getPathWithLanguage = (lang) => {
        const currentPath = location.pathname;
        let pathWithoutLang = currentPath.replace(/^\/[A-Z]{2}(\/|$)/, '/');
        if (pathWithoutLang === '/' || pathWithoutLang === '') {
            return `/${lang}`;
        }
        return `/${lang}${pathWithoutLang}`;
    };

    const prefix = getLocalePrefix();
    const locale = prefix.replace("/", "");
    const t = translations[locale] || translations.GE;

    const isActive = (targetPath) => {
        if (targetPath === prefix && (location.pathname === prefix || location.pathname === `${prefix}/` || location.pathname === '/')) {
            return true;
        }
        return location.pathname === targetPath;
    };

    const handleAboutClick = (e) => {
        const isHomePage = location.pathname === prefix || location.pathname === '/' || location.pathname === `${prefix}/`;
        if (isHomePage) {
            e.preventDefault();
            const aboutEl = document.getElementById('about');
            if (aboutEl) {
                aboutEl.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    return (
        <header 
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 w-full ${
                scrolled 
                    ? 'header-glass-scrolled py-2 md:py-2.5' 
                    : 'header-glass-transparent py-3 md:py-4'
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between">
                {/* Logo - Seamless transparent integration directly onto dark header */}
                <Link 
                    to={prefix} 
                    className="flex items-center group transition-transform duration-300 py-0.5"
                    aria-label="Simi Winery Home"
                >
                    <img 
                        className={`object-contain transition-all duration-500 group-hover:scale-105 group-hover:drop-shadow-[0_0_12px_rgba(180,45,75,0.45)] filter drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)] ${
                            scrolled 
                                ? 'h-12 md:h-14 w-auto' 
                                : 'h-16 md:h-20 w-auto'
                        }`}
                        src={logoTransparent} 
                        alt="Simi Winery Logo" 
                    />
                </Link>

                {/* Desktop Navigation Links */}
                <nav className="hidden md:flex items-center space-x-8 lg:space-x-12">
                    <Link 
                        to={`${prefix}`}
                        className={`font-serif-georgian text-base tracking-wider transition-all duration-300 pb-1 border-b-2 ${
                            isActive(prefix)
                                ? 'text-white border-[#800020] font-semibold'
                                : 'text-stone-300 hover:text-white border-transparent hover:border-[#800020]'
                        }`}
                        style={{ textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}
                    >
                        {t.home}
                    </Link>

                    <a 
                        href={`${prefix}#about`}
                        onClick={handleAboutClick}
                        className="font-serif-georgian text-base tracking-wider text-stone-300 hover:text-white transition-all duration-300 pb-1 border-b-2 border-transparent hover:border-[#800020]"
                        style={{ textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}
                    >
                        {t.about}
                    </a>

                    <Link 
                        to={`${prefix}/products`}
                        className={`font-serif-georgian text-base tracking-wider transition-all duration-300 pb-1 border-b-2 ${
                            isActive(`${prefix}/products`)
                                ? 'text-white border-[#800020] font-semibold'
                                : 'text-stone-300 hover:text-white border-transparent hover:border-[#800020]'
                        }`}
                        style={{ textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}
                    >
                        {t.product}
                    </Link>

                    <a 
                        href="#footer"
                        className="font-serif-georgian text-base tracking-wider text-stone-300 hover:text-white transition-all duration-300 pb-1 border-b-2 border-transparent hover:border-[#800020]"
                        style={{ textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}
                    >
                        {t.contact}
                    </a>
                </nav>

                {/* Desktop Language Selector - Clean, Direct & Intuitive */}
                <div className="hidden md:flex items-center p-1 rounded-full bg-black/40 border border-white/15 backdrop-blur-md shadow-inner">
                    <FontAwesomeIcon icon={faLanguage} className="text-[#800020] text-sm ml-2.5 mr-1.5" />
                    <div className="flex items-center space-x-1">
                        {['GE', 'EN', 'RU'].map(l => (
                            <Link
                                key={l}
                                to={getPathWithLanguage(l)}
                                className={`px-3 py-1 text-xs font-semibold tracking-wider rounded-full transition-all duration-300 ${
                                    locale === l
                                        ? 'bg-[#800020] text-white shadow-md'
                                        : 'text-stone-300 hover:text-white hover:bg-white/10'
                                }`}
                            >
                                {l}
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Mobile Menu Button */}
                <button 
                    className="md:hidden text-stone-100 hover:text-[#800020] p-2 text-2xl focus:outline-none transition-colors"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                >
                    <FontAwesomeIcon icon={menuOpen ? faClose : faBars} />
                </button>
            </div>

            {/* Mobile Dropdown Menu */}
            <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
                menuOpen ? 'max-h-96 opacity-100 border-t border-white/10 mt-3' : 'max-h-0 opacity-0'
            } bg-[#100709]/95 backdrop-blur-2xl shadow-2xl px-6 rounded-b-2xl`}>
                <nav className="flex flex-col space-y-3 py-4 text-center">
                    <Link 
                        to={`${prefix}`}
                        onClick={() => setMenuOpen(false)}
                        className={`font-serif-georgian text-base py-2 border-b transition-colors ${
                            isActive(prefix)
                                ? 'text-white border-[#800020] font-semibold'
                                : 'text-stone-300 hover:text-white border-white/5 hover:border-[#800020]'
                        }`}
                    >
                        {t.home}
                    </Link>
                    <a 
                        href={`${prefix}#about`}
                        onClick={(e) => {
                            setMenuOpen(false);
                            handleAboutClick(e);
                        }}
                        className="font-serif-georgian text-base text-stone-300 hover:text-white py-2 border-b border-white/5 hover:border-[#800020]"
                    >
                        {t.about}
                    </a>
                    <Link 
                        to={`${prefix}/products`}
                        onClick={() => setMenuOpen(false)}
                        className={`font-serif-georgian text-base py-2 border-b transition-colors ${
                            isActive(`${prefix}/products`)
                                ? 'text-white border-[#800020] font-semibold'
                                : 'text-stone-300 hover:text-white border-white/5 hover:border-[#800020]'
                        }`}
                    >
                        {t.product}
                    </Link>
                    <a 
                        href="#footer"
                        onClick={() => setMenuOpen(false)}
                        className="font-serif-georgian text-base text-stone-300 hover:text-white py-2 border-b border-white/5 hover:border-[#800020]"
                    >
                        {t.contact}
                    </a>
                </nav>

                {/* Mobile Language Switcher */}
                <div className="flex justify-center items-center space-x-2.5 py-4 border-t border-white/10">
                    <FontAwesomeIcon icon={faLanguage} className="text-sm text-[#800020] mr-1.5" />
                    {['GE', 'EN', 'RU'].map(l => (
                        <Link 
                            key={l}
                            to={getPathWithLanguage(l)}
                            onClick={() => setMenuOpen(false)}
                            className={`px-3.5 py-1 text-xs rounded-full border transition-all ${
                                locale === l 
                                    ? 'border-[#800020] bg-[#800020] text-white font-bold shadow-md' 
                                    : 'border-white/20 text-stone-300 hover:border-white/50 hover:text-white'
                            }`}
                        >
                            {l}
                        </Link>
                    ))}
                </div>
            </div>
        </header>
    );
}